import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';import { useUnit } from '../contexts/UnitContext';
import { employeeApi, salaryMatrixApi, vacancyApi, masterApi, ALL_UNITS } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Users, Wallet, Briefcase, TrendingUp, ArrowRightLeft, Globe } from 'lucide-react';

const HQ_ROLES = [];  // deprecated — permission-driven
const inr = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);

export default function CorporateHQ() {
  const { user } = useAuth();
  const { setActiveUnit } = useUnit();
  const navigate = useNavigate();
  const [emps, setEmps] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [vacs, setVacs] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Transfer console state
  const [tEmp, setTEmp] = useState('');
  const [tUnit, setTUnit] = useState('');
  const [tDept, setTDept] = useState('');
  const [tMgr, setTMgr] = useState('');
  const [managers, setManagers] = useState([]);
  const [tDate, setTDate] = useState(new Date().toISOString().slice(0, 10));
  const [tNote, setTNote] = useState('');
  const [tBusy, setTBusy] = useState(false);

  useEffect(() => {
    const full = user?.role === 'admin' || user?.perm_scope === 'all';
    if (!full || !(user?.role === 'admin' || (user?.permissions || []).includes('employee.transfer'))) { navigate('/', { replace: true }); return; }
    (async () => {
      setLoading(true);
      const [e, m, v, d] = await Promise.all([
        employeeApi.list({}).catch(() => ({ data: [] })),
        salaryMatrixApi.get({}).catch(() => null),
        vacancyApi.list({}).catch(() => ({ data: [] })),
        masterApi.departments().catch(() => ({ data: [] })),
      ]);
      setEmps(e.data || []); setMatrix(m ? m.data : null); setVacs(v.data || []); setDepts(d.data || []);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (!tUnit) { setManagers([]); setTMgr(''); return; }
    employeeApi.managers({ unit: tUnit }).then((r) => setManagers(r.data || [])).catch(() => setManagers([]));
  }, [tUnit]);

  const latestYear = useMemo(() => {
    const ys = matrix?.years || [];
    return ys.length ? ys[ys.length - 1] : null;
  }, [matrix]);

  const units = useMemo(() => {
    const set = new Set(emps.map((e) => e.unit).filter(Boolean));
    return [...set].sort();
  }, [emps]);

  const perUnit = useMemo(() => {
    const out = {};
    units.forEach((u) => { out[u] = { headcount: 0, depts: new Set(), salary: 0, inc: 0, prior: 0, vacancies: 0 }; });
    emps.forEach((e) => {
      const b = out[e.unit]; if (!b) return;
      b.headcount += 1; if (e.department) b.depts.add(e.department);
    });
    (matrix?.employees || []).forEach((r) => {
      const b = out[r.unit]; if (!b || !latestYear) return;
      const c = r.years?.[latestYear];
      if (c?.gross != null) {
        b.salary += c.gross;
        const inc = c.increment || 0;
        b.inc += inc; b.prior += Math.max(c.gross - inc, 0);
      }
    });
    vacs.forEach((v) => { const b = out[v.unit]; if (b) b.vacancies += 1; });
    return out;
  }, [units, emps, matrix, latestYear, vacs]);

  const deptGrid = useMemo(() => {
    const names = [...new Set(emps.map((e) => e.department).filter(Boolean))].sort();
    const grid = {};
    names.forEach((d) => { grid[d] = {}; units.forEach((u) => { grid[d][u] = 0; }); });
    emps.forEach((e) => { if (e.department && grid[e.department] && e.unit in grid[e.department]) grid[e.department][e.unit] += 1; });
    return { names, grid };
  }, [emps, units]);

  const openUnit = (u) => { setActiveUnit(u); navigate('/employees'); };

  const selEmp = emps.find((e) => e.id === tEmp);
  const doTransfer = async () => {
    if (!selEmp || !tUnit || !tDept || !tDate) { toast.error('Pick employee, destination unit, department and date'); return; }
    if (tUnit === selEmp.unit && tDept === selEmp.department) { toast.error('Destination is the same as current posting'); return; }
    if (!window.confirm(`Transfer ${selEmp.name}: ${selEmp.unit} / ${selEmp.department} → ${tUnit} / ${tDept}?`)) return;
    setTBusy(true);
    try {
      await employeeApi.transfer(selEmp.id, {
        new_unit: tUnit, new_department: tDept, effective_date: tDate,
        note: tNote || null, new_reporting_to: tMgr || null,
      });
      toast.success('Transferred — org chart, master and salary sheet updated');
      const q = new URLSearchParams({ letter: 'transfer', fu: selEmp.unit, tu: tUnit, fd: selEmp.department, td: tDept });
      navigate(`/employees/${selEmp.id}?${q.toString()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transfer failed'); }
    finally { setTBusy(false); }
  };

  const groupHead = emps.length;
  const groupSalary = Object.values(perUnit).reduce((s, b) => s + b.salary, 0);
  const groupVac = vacs.length;

  return (
    <div data-testid="corporate-hq-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1400px] mx-auto">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center text-white"
                   style={{ background: 'linear-gradient(135deg,#0C1B57,#2563EB)' }}><Globe className="w-6 h-6" strokeWidth={1.5} /></div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">Corporate HQ</h1>
                <p className="text-sm text-[#5A6377]">Group-wide control · {units.length} units · {groupHead} employees{latestYear ? ` · ${latestYear} salary ${inr(groupSalary)}` : ''} · {groupVac} open vacancies</p>
              </div>
            </div>

            {loading ? <div className="text-sm text-[#5A6377]">Loading…</div> : (
              <>
                {/* Per-unit cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
                  {units.map((u) => {
                    const b = perUnit[u];
                    const pct = b.prior > 0 ? (b.inc / b.prior) * 100 : null;
                    return (
                      <button key={u} data-testid={`hq-unit-card-${u}`} onClick={() => openUnit(u)}
                        className="bg-white border border-[#E6E9F0] rounded-[14px] p-5 text-left hover:border-[#2563EB] hover:shadow-md transition-all">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-base font-semibold text-[#11182B]">{u}</h3>
                          <span className="text-xs text-[#2563EB] font-medium">Open →</span>
                        </div>
                        <div className="grid grid-cols-2 gap-y-2 text-sm">
                          <div className="flex items-center gap-1.5 text-[#5A6377]"><Users className="w-4 h-4" strokeWidth={1.5} />{b.headcount} people</div>
                          <div className="flex items-center gap-1.5 text-[#5A6377]"><Briefcase className="w-4 h-4" strokeWidth={1.5} />{b.vacancies} vacancies</div>
                          <div className="flex items-center gap-1.5 text-[#5A6377]"><Wallet className="w-4 h-4" strokeWidth={1.5} />{b.salary ? inr(b.salary) : '—'}</div>
                          <div className="flex items-center gap-1.5 text-[#15A36E]"><TrendingUp className="w-4 h-4" strokeWidth={1.5} />{pct != null ? `${pct.toFixed(1)}% incr` : '—'}</div>
                        </div>
                        <div className="text-[11px] text-[#98A1B2] mt-2">{b.depts.size} departments{latestYear ? ` · salary = ${latestYear} gross` : ''}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
                  {/* Unit × Department grid */}
                  <div className="xl:col-span-3 bg-white border border-[#E6E9F0] rounded-[14px] p-5 overflow-auto">
                    <h2 className="text-sm font-semibold text-[#11182B] mb-3">Headcount by department × unit</h2>
                    <table className="text-sm w-full border-collapse">
                      <thead>
                        <tr className="text-xs text-[#5A6377] uppercase">
                          <th className="text-left py-2 pr-3 border-b border-[#E6E9F0]">Department</th>
                          {units.map((u) => <th key={u} className="text-right py-2 px-3 border-b border-[#E6E9F0] whitespace-nowrap">{u}</th>)}
                          <th className="text-right py-2 pl-3 border-b border-[#E6E9F0]">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deptGrid.names.map((d) => {
                          const tot = units.reduce((s, u) => s + deptGrid.grid[d][u], 0);
                          return (
                            <tr key={d} className="border-b border-[#F0F2F7] last:border-0 hover:bg-[#F7F8FB]">
                              <td className="py-1.5 pr-3 text-[#11182B]">{d}</td>
                              {units.map((u) => <td key={u} className="py-1.5 px-3 text-right tabular-nums text-[#5A6377]">{deptGrid.grid[d][u] || ''}</td>)}
                              <td className="py-1.5 pl-3 text-right tabular-nums font-semibold text-[#11182B]">{tot}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Transfer console */}
                  <div className="xl:col-span-2 bg-white border border-[#E6E9F0] rounded-[14px] p-5 h-fit">
                    <h2 className="text-sm font-semibold text-[#11182B] mb-1 flex items-center gap-2"><ArrowRightLeft className="w-4 h-4" strokeWidth={1.5} />Cross-unit transfer</h2>
                    <p className="text-xs text-[#5A6377] mb-4">Updates the employee master, both org charts, salary sheet and ends with the transfer letter.</p>
                    <div className="space-y-3">
                      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Employee</Label>
                        <select data-testid="hq-transfer-emp" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                                value={tEmp} onChange={(e) => { setTEmp(e.target.value); setTUnit(''); setTDept(''); setTMgr(''); }}>
                          <option value="">— select employee —</option>
                          {units.map((u) => (
                            <optgroup key={u} label={u}>
                              {emps.filter((e) => e.unit === u).map((e) => <option key={e.id} value={e.id}>{e.name} — {e.designation}</option>)}
                            </optgroup>
                          ))}
                        </select></div>
                      {selEmp && <div className="text-xs text-[#5A6377]">Current: <span className="font-medium text-[#11182B]">{selEmp.unit} / {selEmp.department}</span></div>}
                      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">To unit</Label>
                        <select data-testid="hq-transfer-unit" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                                value={tUnit} onChange={(e) => { setTUnit(e.target.value); setTMgr(''); }}>
                          <option value="">— select unit —</option>
                          {units.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select></div>
                      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">To department</Label>
                        <select data-testid="hq-transfer-dept" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                                value={tDept} onChange={(e) => setTDept(e.target.value)}>
                          <option value="">— select department —</option>
                          {depts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                        </select></div>
                      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">New manager (in destination unit)</Label>
                        <select data-testid="hq-transfer-mgr" className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full"
                                value={tMgr} onChange={(e) => setTMgr(e.target.value)} disabled={!tUnit}>
                          <option value="">— none yet (place at top, re-point later) —</option>
                          {managers.filter((m) => m.id !== tEmp).map((m) => <option key={m.id} value={m.id}>{m.name} — {m.designation}{m.unit !== tUnit ? ` (${m.unit})` : ''}</option>)}
                        </select></div>
                      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Effective date</Label>
                        <input type="date" data-testid="hq-transfer-date" value={tDate} onChange={(e) => setTDate(e.target.value)}
                               className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full" /></div>
                      <div><Label className="text-xs font-semibold text-[#5A6377] mb-1 block">Note (optional)</Label>
                        <input type="text" data-testid="hq-transfer-note" value={tNote} onChange={(e) => setTNote(e.target.value)} placeholder="Reason / remarks"
                               className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2 w-full" /></div>
                      <div className="flex justify-end pt-1">
                        <Button data-testid="hq-transfer-submit" onClick={doTransfer} disabled={tBusy || !tEmp || !tUnit || !tDept}>
                          {tBusy ? 'Transferring…' : 'Transfer & prepare letter'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
